import { h, Fragment } from 'preact';
import i18n from './I18n';
import { getCompanyProductType, getJewelleryPriceBreakUpConfig } from './Amalgam';
import { getFormattedPrice } from './locale';

const formatStoneDetailsTitle = ({ entityType, entityKeyDetails }) => {
  if (!entityKeyDetails) {
    return '';
  }
  const entityTypeValue = entityType !== 'DIAMOND' ? entityType : undefined;
  const { color, clarity, shape } = entityKeyDetails;
  return [[entityTypeValue, color, clarity].filter((value) => value).join('-'), shape]
    .filter((value) => value)
    .join(' ');
};

export const formatStoneDetailsDescription = ({
  entityKeyDetails,
  hideWeight = false,
  hideRate = false
}) => {
  if (!entityKeyDetails) {
    return '';
  }
  const { pieces, carat, ratePerCt } = entityKeyDetails;
  return [
    !hideWeight
      ? `${carat ? `${carat} ct` : ''}  ${
        pieces ? `(${pieces} pc${pieces === 1 ? '' : 's'})` : ''
      } `
      : '',
    !hideRate && ratePerCt ? `${getFormattedPrice(ratePerCt)}/ct` : ''
  ]
    .filter((value) => value)
    .join(' x ');
};

const formatStoneDiscount = (entityKeyDetails) => {
  const { discount } = entityKeyDetails || {};

  if (!discount) {
    return '';
  }

  return i18n.t('TEN_PERCENT_OFF').replace('10%', discount);
};

export const getJewelleryTypeProductsCharges = ({ productTypeDetails, productTypeRateCharges }) => {
  if (
    getCompanyProductType() !== 'JEWELLERY' ||
    !Array.isArray(productTypeRateCharges) ||
    !productTypeRateCharges.length
  ) {
    return null;
  }

  const metalCharges = [];
  const diamondCharges = [];
  const stoneCharges = [];
  const makingCharges = [];
  const otherCharges = [];

  const jewelleryPriceBreakUpConfig = getJewelleryPriceBreakUpConfig() || {};
  const {
    hideMetalGrossWeightPerGram = false,
    hideMetalNetWeightPerGram = false,
    hideMetalRatePerGram = false,
    hideMakingChargesSection = false,
    hideMakingChargesWeight = false,
    hideMakingChargesRate = false,
    hideDiamondStoneWeight = false,
    hideDiamondStoneRate = false
  } = jewelleryPriceBreakUpConfig;

  productTypeRateCharges.forEach((productTypeRateCharge) => {
    const { id, entityBase, rate, formattedRate, entityType } = productTypeRateCharge;
    const charge = { id, entityBase, rate, formattedRate, entityType };
    const gm = i18n.t('GM');
    const gms = i18n.t('GMS');

    const formatWeight = (weight) => `${weight} ${weight === 1 ? gm : gms}`;

    switch (entityBase) {
      case 'METAL': {
        charge.entityCalculation = productTypeRateCharge.entityCalculation;
        const { grams, perGramCost } = productTypeRateCharge.entityCalculation || {};
        const { grossWeight = (productTypeDetails || {}).grossWeight } =
          productTypeRateCharge.entityDetails || {};
        const { quality = (productTypeDetails || {}).entityQuality, units = 'kt' } =
          productTypeRateCharge.entityDetails || {};

        metalCharges.push({
          ...charge,
          title: `${quality}${units} ${entityType}`,
          description: () => (
            <Fragment>
              {!hideMetalGrossWeightPerGram && grossWeight ? (
                <div className="description-detail">
                  <span className="title">{i18n.t('GROSS_WT')}</span>
                  <span className="separator">:</span>
                  <span className="value">{formatWeight(grossWeight)}</span>
                </div>
              ) : null}
              {!hideMetalNetWeightPerGram && grams ? (
                <div className="description-detail">
                  <span className="title">{i18n.t('NET_WT')}</span>
                  <span className="separator">:</span>
                  <span className="value">{formatWeight(grams)}</span>
                </div>
              ) : null}
              {!hideMetalRatePerGram && perGramCost ? (
                <div className="description-detail">
                  <span className="title">{'Rate' || i18n.t('RATE')}</span>
                  <span className="separator">:</span>
                  <span className="value">{`${getFormattedPrice(perGramCost)}/${gm}`}</span>
                </div>
              ) : null}
            </Fragment>
          )
        });
        return;
      }

      case 'STONE': {
        charge.entityKeyDetails = productTypeRateCharge.entityKeyDetails;
        charge.title = formatStoneDetailsTitle(productTypeRateCharge);
        charge.description = formatStoneDetailsDescription({
          entityKeyDetails: productTypeRateCharge.entityKeyDetails,
          hideWeight: hideDiamondStoneWeight,
          hideRate: hideDiamondStoneRate
        });
        charge.rateWithoutDiscount = productTypeRateCharge.rateWithoutDiscount
          ? productTypeRateCharge.rateWithoutDiscount
          : '';
        charge.discount = formatStoneDiscount(productTypeRateCharge.entityKeyDetails);
        charge.priceDescription = productTypeRateCharge.formattedRateWithoutDiscount
          ? productTypeRateCharge.formattedRateWithoutDiscount
          : '';

        if (entityType === 'DIAMOND') {
          diamondCharges.push(charge);
          return;
        }
        stoneCharges.push(charge);
        return;
      }

      case 'MAKING_CHARGE': {
        charge.entityCalculation = productTypeRateCharge.entityCalculation;
        let { percentFigure, perGramCost, grams } = productTypeRateCharge.entityCalculation || {};
        charge.title = i18n.t('MAKING_CHARGES');
        const getMakingChargeDescription = () => {
          if (hideMakingChargesWeight) {
            grams = undefined;
          }
          if (hideMakingChargesRate) {
            perGramCost = undefined;
          }
          if (percentFigure && (hideMakingChargesWeight || hideMakingChargesRate)) {
            grams = undefined;
            perGramCost = undefined;
            percentFigure = undefined;
          }
          if (!percentFigure && !grams && !perGramCost) {
            return undefined;
          }
          if (percentFigure) {
            return `(${percentFigure} ${i18n.t('OF_METAL_CHARGES')})`;
          }
          if (!grams && perGramCost) {
            return `${getFormattedPrice(perGramCost)}/${gm}`;
          }
          if (grams && !perGramCost) {
            return `${formatWeight(grams)}`;
          }
          return `${formatWeight(grams)} x ${getFormattedPrice(perGramCost)}/${gm}`;
        };
        charge.description = getMakingChargeDescription();
        if (!hideMakingChargesSection) {
          makingCharges.push(charge);
        }
        return;
      }

      default: {
        charge.title = entityType;
        otherCharges.push(charge);
      }
    }
  });

  return {
    metalCharges,
    diamondCharges,
    stoneCharges,
    otherCharges,
    makingCharges
  };
};
